/**
 * The evidence slip — one of the design's two signature elements.
 *
 * Ported from the demo, with the change real data forces: the demo drew the same fake screenshot
 * beneath every finding. Real findings are backed by two different kinds of capture, and D-012
 * requires each to say which it is and never to be shown as the other.
 *
 *   `document`       a fetched file — a sitemap, robots.txt. Shows the stored artifact and its
 *                    digest. No screenshot exists, and none is drawn.
 *   `rendered_page`  a page rendered in a browser. Shows the full-page screenshot, loaded
 *                    through a short-expiry signed URL.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { Evidence, ReportFinding } from '@mintro/engine';
import type { EvidenceAccess } from '../lib/evidence.js';
import { shortHash, formatStamp } from '../lib/format.js';

interface Props {
  readonly finding: ReportFinding;
  readonly access: EvidenceAccess;
  /**
   * Folds the capture's provenance lines under one summary (cluster 4b commit 3).
   *
   * Source, method and digest are what make a finding checkable and they are read by very few
   * readers, in that order: the sentence, then the capture, then — for the one reader who is
   * checking — where it came from. Folded, never dropped: `<details>` keeps them in the document and
   * in the delivered file, and `open` is what the print route passes so the captured bytes show them.
   *
   * Absent on the peptide report, which renders exactly as it did.
   */
  readonly fold?: { readonly label: string; readonly open?: boolean };
}

/**
 * The matched URLs the observation has not already named (D-215).
 *
 * NAME-002's sentence reads *"…matched a prohibited pattern: /shop/bpc-157-tb500-blend/ (matched
 * 'blend'); /shop/cjc-1295-no-dac-ipamorelin-blend/ (matched 'blend')"* and the slip then listed
 * those same two URLs underneath with no label. GATE-002 did the same with one. Printing a list a
 * reader has just read, unlabelled, is what made it look like a second and different fact.
 *
 * Filtered rather than dropped: where the note does *not* name them — a rule that matched more URLs
 * than its sentence enumerates — the list is the only place they appear and it stays.
 */
export function unlistedUrls(evidence: Evidence, note: string): readonly string[] {
  return (evidence.matchedUrls ?? []).filter((url) => !note.includes(url));
}

export function EvidenceSlip({ finding, access, fold }: Props): JSX.Element {
  // The richest evidence entry — the one carrying a matched value — is the one worth leading
  // with. Findings that observed nothing carry only the source reference.
  const primary =
    finding.evidence.find((entry) => entry.matchedValue !== undefined) ?? finding.evidence[0];

  if (primary === undefined) {
    return (
      <div className="slip">
        <div className="slip-bar">
          <span className="eyebrow">Evidence</span>
        </div>
        <div className="slip-body">
          <div className="slip-txt">
            {/*
              Two words, not two sentences (D-167).

              It read "This finding carries no stored evidence. It was produced before any surface
              was reached" under every finding of this shape — on these runs, every attestation
              rule. The second sentence is the same statement the section lede already makes about
              all of them, and the first is what the label says. What a reader needs here is that
              there is no capture, which is now what it says.
            */}
            <div className="why">
              <b>No capture</b>
              Nothing was retrieved for this rule, so there is nothing to cite.
            </div>
            {/*
              The clause, only where nothing else carries it (D-047).

              Every non-pass finding shows it verbatim in the Program requirement column above,
              so repeating it here is the second of two identical quotations. A `pass` has no
              requirement pair — D-041 leaves it out, since a satisfied rule quoted back at the
              reader is noise — so for those this is the only place it appears and it stays.
            */}
            {finding.state === 'pass' && (
              <div className="rule-ref">
                <b>Rule.</b> {finding.clause}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="slip">
      <div className="slip-bar">
        <span className="eyebrow">Evidence</span>
        <span className={`ev-kind ${primary.kind}`}>
          {primary.kind === 'document' ? 'stored document' : 'rendered page'}
        </span>
        <span className="stamp">captured {formatStamp(primary.capturedAt)}</span>
      </div>

      <div className="slip-body">
        <div className="slip-txt">
          {/*
            The observation is not repeated here (D-047).

            It is stated once, in the Observed column of the requirement pair directly above. It
            used to appear three times per finding — row heading, Observed column, and here — and
            the clause twice. Nothing is lost by removing the repeats: the pairing D-041 requires
            is intact, and this slip carries what only it has, which is the capture.
          */}
          <Provenance fold={fold}>
            <div className="kv">
              <span className="k">Source</span>
              <span className="v">{primary.sourceUrl}</span>
            </div>
            <div className="kv">
              <span className="k">Method</span>
              <span className="v">
                {primary.kind === 'document'
                  ? 'fetched document · no browser'
                  : 'rendered DOM · headless Chromium'}
              </span>
            </div>
            {primary.sourceSha256 !== '' && (
              <div className="kv">
                <span className="k">SHA-256</span>
                <span className="v">{shortHash(primary.sourceSha256)}</span>
              </div>
            )}
          </Provenance>

          {primary.matchedValue !== undefined && (
            <div className="capture">
              {/*
                The payload says what it is (D-215).

                It rendered as a bare string in a box: under NAME-002 the word `blend` on its own,
                under GATE-002 `200 https://…/shop` followed by an unlabelled list of URLs. Every
                other line in this slip names its field — Source, Method, SHA-256, Requests
                attempted — and this one, the only one carrying what the rule actually matched, did
                not. A reader who cannot tell whether `blend` is the matched value, the pattern, or
                a fragment of the page cannot check the finding.

                A prefix on the text, not a new row: the label is the string, so the slip's shape is
                untouched. `DeclineNotice` already writes it this way.
              */}
              <span className="matched">Matched: {primary.matchedValue}</span>
              {unlistedUrls(primary, finding.note).length > 0 && (
                <ul className="matched-urls">
                  {unlistedUrls(primary, finding.note)
                    .slice(0, 8)
                    .map((url) => (
                      <li key={url}>{url}</li>
                    ))}
                  {unlistedUrls(primary, finding.note).length > 8 && (
                    <li>…and {unlistedUrls(primary, finding.note).length - 8} more</li>
                  )}
                </ul>
              )}
            </div>
          )}

          {/*
            The requests attempted, and what they returned.

            The *reason* a rule could not be evaluated is stated in the Not assessed column above
            and is not repeated (D-047). This is the part hard constraint 3 requires and only the
            slip has: a `not_evaluable` finding must evidence why, with the requests attempted.
          */}
          {primary.attempts !== undefined && primary.attempts.length > 0 && (
            <div className="why">
              <b>Requests attempted</b>
              <ul className="matched-urls">
                {primary.attempts.map((attempt) => (
                  <li key={`${attempt.url}-${attempt.status}`}>
                    {attempt.url} → {attempt.status === 0 ? (attempt.error ?? 'no response') : attempt.status}
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>

        <CapturePane evidence={primary} access={access} />
      </div>
    </div>
  );
}

/**
 * Where the capture came from: as it was, or folded under one line (cluster 4b commit 3).
 *
 * The same rows either way. A fold that rendered different content from the open form would be a
 * document that says one thing on screen and another in the file.
 */
function Provenance({
  fold,
  children,
}: {
  readonly fold: { readonly label: string; readonly open?: boolean } | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  if (fold === undefined) return <>{children}</>;

  return (
    <details className="cap-fold" open={fold.open === true}>
      <summary>{fold.label}</summary>
      {children}
    </details>
  );
}

/**
 * The right-hand capture.
 *
 * A rendered finding gets its screenshot. A documentary finding gets the stored artifact named —
 * never a screenshot placeholder, which would suggest a visual capture that does not exist.
 */
function CapturePane({
  evidence,
  access,
}: {
  readonly evidence: Evidence;
  readonly access: EvidenceAccess;
}): JSX.Element {
  if (evidence.kind === 'document') {
    return (
      <div className="doc-artifact">
        <span className="gl">▤</span>
        <span className="lbl">
          stored document
          <br />
          {evidence.evidenceKey === '' ? 'not retained' : shortHash(evidence.sourceSha256)}
        </span>
      </div>
    );
  }
  return <Screenshot evidenceKey={evidence.evidenceKey} access={access} />;
}

/** A screenshot, fetched through a freshly minted signed URL each time it is shown. */
function Screenshot({
  evidenceKey,
  access,
}: {
  readonly evidenceKey: string;
  readonly access: EvidenceAccess;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    if (evidenceKey === '') {
      setFailed(true);
      return;
    }
    void access.urlFor(evidenceKey).then((signed) => {
      if (!live) return;
      if (signed === null) setFailed(true);
      else setUrl(signed);
    });
    return () => {
      live = false;
    };
  }, [evidenceKey, access]);

  return (
    <div className="shot">
      <div className="shot-frame">
        <div className="shot-bar">
          <span />
          <span />
          <span />
        </div>
        {url !== null && !failed ? (
          <img
            className="shot-img"
            src={url}
            alt="Full-page screenshot captured during the run"
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="shot-missing">
            {failed ? 'capture not reachable' : 'loading capture…'}
          </div>
        )}
      </div>
      <div className="shot-cap">full-page PNG</div>
    </div>
  );
}
